import {
  Archive,
  ArchiveRestore,
  Building2,
  Check,
  ClipboardCheck,
  LoaderCircle,
  MapPin,
  Pencil,
  Phone,
  Plus,
  ShieldCheck,
  X,
} from "lucide-react";
import { useCallback, useEffect, useState, type FormEvent, type ReactNode } from "react";
import { api, type CrmLocation } from "./api";
import { useCrm } from "./crm-context";

function messageFor(reason: unknown, fallback: string): string {
  return reason instanceof Error ? reason.message : fallback;
}

function canManageLocations(role: string | undefined): boolean {
  return role === "OWNER" || role === "ADMIN";
}

export function LocationsScreen(): ReactNode {
  const { activeOrganization } = useCrm();
  const [locations, setLocations] = useState<CrmLocation[]>([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState<CrmLocation | null>(null);
  const [name, setName] = useState("");
  const [address, setAddress] = useState("");
  const [phone, setPhone] = useState("");
  const [pendingId, setPendingId] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const canManage = canManageLocations(activeOrganization?.role);

  const load = useCallback(async () => {
    if (!activeOrganization) return;
    setLoading(true);
    setError(null);
    try {
      setLocations(await api.locations(activeOrganization.id, true));
    } catch (reason) {
      setError(messageFor(reason, "No pudimos cargar las sedes de la organización."));
    } finally {
      setLoading(false);
    }
  }, [activeOrganization?.id]);

  useEffect(() => {
    void load();
  }, [load]);

  function resetForm(): void {
    setEditing(null);
    setName("");
    setAddress("");
    setPhone("");
    setShowForm(false);
  }

  function beginCreate(): void {
    setError(null);
    setNotice(null);
    setEditing(null);
    setName("");
    setAddress("");
    setPhone("");
    setShowForm(true);
  }

  function beginEdit(location: CrmLocation): void {
    setError(null);
    setNotice(null);
    setEditing(location);
    setName(location.name);
    setAddress(location.address ?? "");
    setPhone(location.phone ?? "");
    setShowForm(true);
  }

  async function submit(event: FormEvent): Promise<void> {
    event.preventDefault();
    if (!activeOrganization || !canManage) return;
    const trimmedName = name.trim();
    if (!trimmedName) return;
    setPendingId(editing?.id ?? "new");
    setError(null);
    setNotice(null);
    try {
      const input = {
        name: trimmedName,
        address: address.trim() || null,
        phone: phone.trim() || null,
      };
      if (editing) {
        const updated = await api.updateLocation(activeOrganization.id, editing.id, input);
        setLocations((current) => current.map((item) => (item.id === updated.id ? updated : item)));
        setNotice(`${updated.name} quedó actualizada.`);
      } else {
        const created = await api.createLocation(activeOrganization.id, input);
        setLocations((current) =>
          [...current, created].sort((left, right) => left.name.localeCompare(right.name)),
        );
        setNotice(`${created.name} ya está disponible para agendar turnos.`);
      }
      resetForm();
    } catch (reason) {
      setError(messageFor(reason, "No pudimos guardar la sede."));
    } finally {
      setPendingId(null);
    }
  }

  async function changeAvailability(location: CrmLocation): Promise<void> {
    if (!activeOrganization || !canManage) return;
    const activating = !location.isActive;
    if (
      !activating &&
      !window.confirm(
        `¿Archivar ${location.name}? Los turnos históricos la conservarán como referencia, pero no se podrá elegir para nuevos turnos.`,
      )
    ) {
      return;
    }
    setPendingId(location.id);
    setError(null);
    setNotice(null);
    try {
      const updated = await api.updateLocation(activeOrganization.id, location.id, {
        isActive: activating,
      });
      setLocations((current) => current.map((item) => (item.id === updated.id ? updated : item)));
      setNotice(
        activating
          ? `${updated.name} volvió a estar disponible para nuevos turnos.`
          : `${updated.name} quedó archivada. La agenda histórica no se modificó.`,
      );
    } catch (reason) {
      setError(messageFor(reason, "No pudimos cambiar el estado de la sede."));
    } finally {
      setPendingId(null);
    }
  }

  if (!activeOrganization) return null;

  const activeCount = locations.filter((location) => location.isActive).length;
  return (
    <div className="screen locations-screen">
      <header className="page-header">
        <div>
          <span className="eyebrow">Configuración operativa</span>
          <h1>Sedes</h1>
          <p>
            La agenda, Jornada y los reportes conservan el contexto correcto de cada consultorio.
          </p>
        </div>
        {canManage && (
          <button className="button button-primary" type="button" onClick={beginCreate}>
            <Plus size={17} /> Nueva sede
          </button>
        )}
      </header>

      {error && (
        <div className="error-panel" role="alert">
          <Building2 size={20} aria-hidden="true" />
          <div>
            <strong>No pudimos completar la operación</strong>
            <p>{error}</p>
          </div>
          <button
            className="button button-secondary button-compact"
            type="button"
            onClick={() => void load()}
          >
            Reintentar
          </button>
        </div>
      )}
      {notice && (
        <div className="success-panel" role="status">
          <Check size={18} aria-hidden="true" />
          <span>{notice}</span>
          <button
            className="icon-button"
            type="button"
            onClick={() => setNotice(null)}
            aria-label="Cerrar aviso"
          >
            <X size={16} />
          </button>
        </div>
      )}

      <section className="location-intro" aria-label="Cobertura operativa">
        <span className="location-intro-icon" aria-hidden="true">
          <ClipboardCheck size={21} />
        </span>
        <div>
          <strong>{activeCount === 1 ? "Una sede activa" : `${activeCount} sedes activas`}</strong>
          <p>
            Una sede archivada no se borra: los turnos ya creados conservan su referencia para
            tutor, equipo y auditoría.
          </p>
        </div>
        <span className="location-intro-status">
          <ShieldCheck size={16} aria-hidden="true" /> Tenant protegido
        </span>
      </section>

      {showForm && (
        <form className="location-editor panel" onSubmit={(event) => void submit(event)}>
          <div className="panel-heading">
            <div>
              <span className="eyebrow">{editing ? "Edición controlada" : "Nueva cobertura"}</span>
              <h2>{editing ? `Editar ${editing.name}` : "Registrar sede"}</h2>
            </div>
            <button
              className="icon-button"
              type="button"
              onClick={resetForm}
              aria-label="Cerrar formulario de sede"
            >
              <X size={18} />
            </button>
          </div>
          <div className="form-grid">
            <label>
              Nombre de la sede
              <input
                value={name}
                onChange={(event) => setName(event.target.value)}
                minLength={2}
                maxLength={100}
                autoFocus
                required
                placeholder="Ej. Sede Palermo"
              />
            </label>
            <label>
              Teléfono interno o de recepción
              <input
                value={phone}
                onChange={(event) => setPhone(event.target.value)}
                maxLength={40}
                inputMode="tel"
                placeholder="Opcional"
              />
            </label>
            <label className="form-span-2">
              Dirección o referencia
              <input
                value={address}
                onChange={(event) => setAddress(event.target.value)}
                maxLength={240}
                placeholder="Opcional. Se mostrará como referencia del turno."
              />
            </label>
          </div>
          <p className="form-note">
            Los cambios no reescriben los turnos existentes: cada turno conserva una referencia de
            la sede al momento de su creación.
          </p>
          <div className="form-actions">
            <button
              className="button button-secondary"
              type="button"
              onClick={resetForm}
              disabled={pendingId !== null}
            >
              Cancelar
            </button>
            <button className="button button-primary" type="submit" disabled={pendingId !== null}>
              {pendingId ? <LoaderCircle className="spin" size={17} /> : <Check size={17} />}
              {editing ? "Guardar cambios" : "Crear sede"}
            </button>
          </div>
        </form>
      )}

      {loading ? (
        <div className="list-skeleton location-skeleton" role="status" aria-label="Cargando sedes">
          <span />
          <span />
          <span />
        </div>
      ) : locations.length ? (
        <section className="locations-grid" aria-label="Sedes de la organización">
          {locations.map((location) => {
            const busy = pendingId === location.id;
            return (
              <article
                className={`location-card${location.isActive ? "" : " is-archived"}`}
                key={location.id}
              >
                <div className="location-card-heading">
                  <span className="location-marker" aria-hidden="true">
                    <Building2 size={20} />
                  </span>
                  <div>
                    <div>
                      <h2>{location.name}</h2>
                      <span
                        className={`status ${location.isActive ? "status-active" : "status-cancelled"}`}
                      >
                        {location.isActive ? "Disponible" : "Archivada"}
                      </span>
                    </div>
                    <small>{location.timezone}</small>
                  </div>
                </div>
                <dl className="location-details">
                  <div>
                    <dt>
                      <MapPin size={15} aria-hidden="true" /> Referencia
                    </dt>
                    <dd>{location.address ?? "Sin dirección registrada"}</dd>
                  </div>
                  <div>
                    <dt>
                      <Phone size={15} aria-hidden="true" /> Contacto
                    </dt>
                    <dd>{location.phone ?? "Sin teléfono registrado"}</dd>
                  </div>
                </dl>
                {canManage && (
                  <div className="location-card-actions">
                    <button
                      className="button button-secondary button-compact"
                      type="button"
                      onClick={() => beginEdit(location)}
                      disabled={busy}
                    >
                      <Pencil size={16} /> Editar
                    </button>
                    <button
                      className="button-link"
                      type="button"
                      onClick={() => void changeAvailability(location)}
                      disabled={busy}
                    >
                      {busy ? (
                        <LoaderCircle className="spin" size={16} />
                      ) : location.isActive ? (
                        <Archive size={16} />
                      ) : (
                        <ArchiveRestore size={16} />
                      )}
                      {location.isActive ? "Archivar" : "Reactivar"}
                    </button>
                  </div>
                )}
              </article>
            );
          })}
        </section>
      ) : (
        <section className="empty-state locations-empty">
          <span aria-hidden="true">
            <Building2 size={28} />
          </span>
          <h2>Todavía no hay sedes configuradas</h2>
          <p>
            Podés seguir usando ubicaciones libres, o crear la primera sede para ordenar agenda y
            reportes.
          </p>
          {canManage && (
            <button className="button button-primary" type="button" onClick={beginCreate}>
              <Plus size={17} /> Crear primera sede
            </button>
          )}
        </section>
      )}
    </div>
  );
}
